import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource, useFileDownload } from '@app/src/api/fetchHooks'
import { useMenuState } from '@app/src/hooks/mui-hooks'
import AutoTranslatedAlert from '@app/src/pages/Questionnaire/Provider/Report/AutoTranslatedAlert'
import ProviderReportTranslation from '@app/src/pages/Questionnaire/Provider/Report/ProviderReportTranslation'
import type { LanguageInfo } from '@app/src/pages/Questionnaire/ReportContext'
import { useReport } from '@app/src/pages/Questionnaire/ReportContext'
import { ONE_HOUR_MS } from '@app/src/wf-constants'
import { Download } from '@mui/icons-material'
import { Box, Button, CircularProgress, Menu, MenuItem, Stack } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import UploadFiles from './UploadFiles'

export enum AutoAssignTranslationState {
  NoAutoAssignedLanguage = 'NoAutoAssignedLanguage',
  LanguageAutoAssigned = 'LanguageAutoAssigned',
  LanguageAutoAssignedButReverted = 'LanguageAutoAssignedButReverted',
  LanguageAutoAssignedButChangedToSomethingElse = 'LanguageAutoAssignedButChangedToSomethingElse',
}

interface ReportHeaderProps {
  generatePDF: () => Promise<void>
  isPdfLoading: boolean
  isRequestLoading: boolean
}

const ReportHeader: React.FC<ReportHeaderProps> = ({ generatePDF, isPdfLoading, isRequestLoading }) => {
  const { response, locale } = useReport()
  const { formatMessage } = useIntl()
  const [isMenuOpen, menuAnchorElement, openMenu, closeMenu] = useMenuState()
  const { downloadFile } = useFileDownload()
  const [autoAssignTranslationState, setAutoAssignTranslationState] = useState<AutoAssignTranslationState>(
    AutoAssignTranslationState.NoAutoAssignedLanguage,
  )

  const {
    data: availableLanguagesForReport,
    isLoading,
    isError,
  } = useFetchResource<LanguageInfo[]>({
    endpoint: endpoints.availableLanguageTranslation,
    key: FetchKey.Languages,
    options: {
      staleTime: ONE_HOUR_MS,
    },
    shouldShowErrorNotification: false,
  })

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={3}>
        <ProviderReportTranslation
          autoAssignTranslationState={autoAssignTranslationState}
          setAutoAssignTranslationState={setAutoAssignTranslationState}
          availableLanguagesForReport={availableLanguagesForReport}
          isLoading={isLoading}
          isError={isError}
        />
        <Stack
          data-pdf-hide
          direction={{ xs: 'column', sm: 'row', md: 'column', lg: 'row' }}
          spacing={1}
          flexWrap="wrap"
        >
          <UploadFiles />
          <Box>
            <Button
              startIcon={isPdfLoading ? <CircularProgress size={16} /> : <Download />}
              onClick={openMenu}
              disabled={isPdfLoading || isRequestLoading}
              sx={{ px: 2 }}
            >
              {formatMessage({ id: 'general.download' })}
            </Button>
            <Menu anchorEl={menuAnchorElement} open={isMenuOpen} onClose={closeMenu}>
              <MenuItem
                onClick={() => {
                  closeMenu()
                  generatePDF()
                }}
              >
                {formatMessage({ id: 'resourceCollections.general.exportToPdf' })}
              </MenuItem>
              {response?.id && (
                <MenuItem
                  onClick={() => {
                    closeMenu()
                    downloadFile(endpoints.downloadResponse(response.id, locale ? locale.code : ''), 'xlsx')
                  }}
                >
                  {formatMessage({ id: 'reporting.downloadExcel' })}
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Stack>
      </Stack>
      <AutoTranslatedAlert
        autoAssignTranslationState={autoAssignTranslationState}
        setAutoAssignTranslationState={setAutoAssignTranslationState}
        availableLanguagesForReport={availableLanguagesForReport}
      />
    </>
  )
}

export default ReportHeader
